import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../services/apiClient';
import StakeTable, { Column } from './GameEngine/StakeTable';
import { formatDateTime, formatDate } from '../utils/dateUtils';

// ─── Types ───────────────────────────────────────────────────────────────────
interface ProfileData {
  id: number; name: string; email: string; password2?: string;
  balance: string; total_deposit: string; total_withdrawal: string;
  total_bets?: number; total_bet_amount?: string; total_win_amount?: string;
  created?: string; referalcode?: string; ip_address?: string; geo?: string;
  vip_level?: number;
}
interface ReferralData {
  f1: number; f2: number; f3: number; f4: number; f5: number; f6: number;
  total_invitations: number;
}
interface Transaction {
  id: number; amount: string; status?: string; req_type?: string;
  transaction_date?: string; utr_number?: string; payment_method?: string;
  txn_id?: string; type?: string; round_id?: string; game_id?: number;
  is_cancel?: number; created_at?: string;
}
interface CurrencyStat {
  currency: string;
  balance: string;
  total_deposit: string;
  total_withdrawal: string;
  total_bets: number;
  total_bet_amount: string;
  total_win_amount: string;
}
interface VipData {
  current_level: number;
  current_tier: { level_number: number; level_name: string; badge_color: string; tier: string };
  next_tier: { level_number: number; level_name: string; condition_type: string; required_amount: string; required_deposit?: string; badge_color: string } | null;
  total_wagered_usd: number;
  total_deposit_usd: number;
  wager_progress: number;
  deposit_progress: number;
  overall_progress: number;
  all_levels: Array<{ level_number: number; level_name: string; badge_color: string }>;
}
interface UserDetailsResponse {
  profile: ProfileData;
  currencies?: CurrencyStat[];
  referral: ReferralData;
  vip?: VipData;
  transactions: { data: Transaction[]; total: number; page: number; limit: number };
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
const statusCfg: Record<string, string> = {
  accepted: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  rejected:  'bg-red-500/10 border-red-500/20 text-red-400',
  pending:   'bg-amber-500/10 border-amber-500/20 text-amber-400',
  win:       'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  bet:       'bg-sky-500/10 border-sky-500/20 text-sky-400',
};
const Tag: React.FC<{ label?: string }> = ({ label }) => {
  const key = label?.toLowerCase() || 'pending';
  const cls = statusCfg[key] || 'bg-slate-700 border-slate-600 text-slate-400';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold border ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {label || '—'}
    </span>
  );
};

const formatUSD = (val: number | string | undefined): string => {
  const num = typeof val === 'number' ? val : parseFloat(val?.toString() || '0');
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  }).format(num);
};

// ─── Component ────────────────────────────────────────────────────────────────
const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [activeTab, setActiveTab] = useState('deposits');
  const [subTab, setSubTab]       = useState('all');
  const [page, setPage]           = useState(1);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');
  const limit = 20;

  // ── Single query that re-fetches when tab / subTab / page changes ──────────
  const { data, isLoading, isFetching, refetch } = useQuery<UserDetailsResponse>({
    queryKey: ['userProfile', id, activeTab, subTab, page],
    queryFn: async () => {
      const res = await apiClient.get(
        `/admin/getUserDetails/${id}?tab=${activeTab}&status=${subTab}&page=${page}&limit=${limit}`
      );
      return res.data.data as UserDetailsResponse;
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const profile = data?.profile;
  const referral = data?.referral;
  const txData = data?.transactions;

  // Query VIP details using the exact same calculation and endpoints as the frontend Home page
  const { data: vipFromProfile } = useQuery({
    queryKey: ['userVipProfileData', id],
    queryFn: async () => {
      try {
        const [profRes, lvlRes] = await Promise.all([
          apiClient.get(`/api/myprofile/${id}`),
          apiClient.get('/admin/getLevel')
        ]);
        const u = profRes.data?.data?.[0];
        const lvls = lvlRes.data?.data || [];
        if (!u) return null;

        const vipLevelNum = parseInt(u.vip_level || 0, 10);
        const totalWageredUsd = parseFloat(u.total_wagered_usd || u.total_bet_amount || 0);
        const totalDepositUsd = parseFloat(u.total_deposit_amount || 0);

        const sorted = [...lvls].sort((a: any, b: any) => a.level_number - b.level_number);
        const cur = sorted.find((l: any) => l.level_number === vipLevelNum) || sorted[0] || { level_number: 1, level_name: 'Bronze', badge_color: '#cd7f32' };
        const nxt = sorted.find((l: any) => l.level_number > (vipLevelNum || 0)) || null;

        let wagerPct = 100;
        let depPct = 100;
        let overallPct = 100;

        if (nxt) {
          const toWager = parseFloat(nxt.required_amount || 0);
          wagerPct = toWager > 0 ? Math.min(100, Math.max(0, (totalWageredUsd / toWager) * 100)) : 0;

          const toDep = parseFloat(nxt.required_deposit || nxt.required_amount || 0);
          depPct = toDep > 0 ? Math.min(100, Math.max(0, (totalDepositUsd / toDep) * 100)) : 0;

          overallPct = nxt.condition_type === 'both' ? Math.min(wagerPct, depPct) : (nxt.condition_type === 'deposit' ? depPct : wagerPct);
        }

        return {
          current_level: vipLevelNum,
          current_tier: cur,
          next_tier: nxt,
          total_wagered_usd: totalWageredUsd,
          total_deposit_usd: totalDepositUsd,
          wager_progress: Math.round(wagerPct * 10) / 10,
          deposit_progress: Math.round(depPct * 10) / 10,
          overall_progress: Math.round(overallPct * 10) / 10,
          all_levels: sorted
        };
      } catch (err) {
        console.error('Failed to load myprofile for VIP:', err);
        return null;
      }
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always'
  });

  const vipData = data?.vip || vipFromProfile;

  const totalPages = Math.max(1, Math.ceil((txData?.total ?? 0) / limit));

  const currenciesList = data?.currencies || [];
  const activeCurrency = selectedCurrency || (currenciesList[0]?.currency ?? '');
  const activeCurrencyStat = currenciesList.find((c: CurrencyStat) => c.currency === activeCurrency);

  const getGatewayDisplayLabel = (name: string | undefined): string => {
    if (!name) return 'Manual';
    const labelMap: { [key: string]: string } = {
      'manual_bank': 'Bank Details',
      'manual_upi_wallet': 'UPI / Wallet',
    };
    return labelMap[name.toLowerCase()] || name;
  };

  const getMethodLabel = (provider: string | undefined): string => {
    if (!provider) return 'N/A';
    const labelMap: { [key: string]: string } = {
      'bdpay': 'BD Pay',
      'provider2': 'Wallet',
      'wallet': 'Wallet',
      'bkash': 'bKash',
      'manual': 'Manual',
      'manual_qr': 'Manual QR',
      'manual_upi': 'Manual UPI',
      'manualb_bank': 'Manual Bank',
    };
    return labelMap[provider.toLowerCase()] || provider;
  };

  // ── Columns: Deposits ───────────────────────────────────────────────────────
  const depositColumns: Column[] = [
    { key: 'id',               label: '#',      render: (_v, _r, i) => (page - 1) * limit + i + 1 },
    { key: 'amount',           label: 'Amount', render: (val) => <span className="font-bold text-white tabular-nums">{Number(val).toFixed(2)}</span> },
    { key: 'currency',         label: 'Currency', render: (_, item: any) => <span className="font-semibold text-slate-300">{(item.currency || item.transaction_type || 'INR').toUpperCase()}</span> },
    {
      key: 'gateway_name',
      label: 'Gateway Name',
      render: (_, item: any) => {
        const gName = item.gateway_name || item.payment_method || 'Manual';
        if (gName && gName !== 'Manual') return gName;
        const provider = item.payment_provider || item.transaction_type || '';
        if (provider.startsWith('manual')) {
          const parts = provider.split('_');
          if (parts.length > 1) {
            return parts.slice(1).map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
          }
          return 'Manual';
        }
        return provider.charAt(0).toUpperCase() + provider.slice(1) || 'Manual';
      }
    },
    { key: 'gateway_method', label: 'Gateway Method', render: (_, item: any) => getMethodLabel(item.gateway_method || item.payment_provider || item.payment_method) },
    { key: 'utr_number',       label: 'TransactionNo / UTR',    render: (v, item: any) => <span className="font-mono text-xs">{v || item.utr_number || item.utr_no || '—'}</span> },
    {
      key: 'status',
      label: 'Status',
      render: (val, item: any) => (
        <div className="flex flex-col gap-1">
          <Tag label={val} />
          {val === 'rejected' && item.reject_reason && (
            <span className="text-[10px] text-red-400 italic max-w-[150px] truncate block" title={item.reject_reason}>
              Reason: {item.reject_reason}
            </span>
          )}
        </div>
      )
    },
    { key: 'transaction_date', label: 'Date',   render: v => formatDateTime(v) },
  ];

  // ── Columns: Withdrawals ────────────────────────────────────────────────────
  const withdrawalColumns: Column[] = [
    { key: 'id',               label: '#',      render: (_v, _r, i) => (page - 1) * limit + i + 1 },
    { key: 'amount',           label: 'Amount', render: (val) => <span className="font-bold text-white tabular-nums">{Number(val).toFixed(2)}</span> },
    { key: 'currency',         label: 'Currency', render: (_, item: any) => <span className="font-semibold text-slate-300">{(item.currency || item.transaction_type || 'INR').toUpperCase()}</span> },
    { key: 'gateway_name',     label: 'Method', render: (val) => <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-xs font-semibold">{getGatewayDisplayLabel(val)}</span> },
    { key: 'withdraw_method', label: 'Withdrawal Details', render: (_, item: any) => {
      let fields: { [key: string]: any } = {};
      try {
        const details = typeof item.payment_details === 'string'
          ? JSON.parse(item.payment_details)
          : item.payment_details;
        if (details && details.extra_fields) {
          fields = details.extra_fields;
        }
      } catch (e) {}

      const keys = Object.keys(fields).filter(k => k !== 'currency');
      if (keys.length > 0) {
        return (
          <div className="flex flex-col gap-0.5 text-[11px] text-slate-300 font-mono">
            {keys.map(k => (
              <div key={k} className="truncate max-w-[240px]" title={`${k.replace(/_/g, ' ').toUpperCase()}: ${fields[k]}`}>
                <span className="font-semibold text-indigo-400">{k.replace(/_/g, ' ').toUpperCase()}:</span> {fields[k]}
              </div>
            ))}
          </div>
        );
      }

      return (
        <div className="max-w-xs truncate text-xs text-slate-300 font-mono" title={item.withdraw_method}>
          {item.withdraw_method || 'N/A'}
        </div>
      );
    } },
    { key: 'utr_number',       label: 'Transaction ID / UTR',    render: (v, item: any) => <span className="font-mono text-xs">{v || item.utr_number || item.utr_no || '—'}</span> },
    {
      key: 'status',
      label: 'Status',
      render: (val, item: any) => (
        <div className="flex flex-col gap-1">
          <Tag label={val} />
          {val === 'rejected' && item.reject_reason && (
            <span className="text-[10px] text-red-400 italic max-w-[150px] truncate block" title={item.reject_reason}>
              Reason: {item.reject_reason}
            </span>
          )}
        </div>
      )
    },
    { key: 'transaction_date', label: 'Date',   render: v => formatDateTime(v) },
  ];

  // ── Columns: Bets ─────────────────────────────────────────────────────────
  const betColumns: Column[] = [
    { key: 'id',         label: '#',         render: (_v, _r, i) => (page - 1) * limit + i + 1 },
    { key: 'txn_id',     label: 'Txn ID',    render: v => <span className="font-mono text-xs">{v || '—'}</span> },
    { key: 'amount',     label: 'Amount',    render: v => <span className="font-bold text-white tabular-nums">{v}</span> },
    { key: 'type',       label: 'Type',      render: v => <Tag label={v} /> },
    { key: 'round_id',   label: 'Round ID',  render: v => <span className="font-mono text-xs">{v || '—'}</span> },
    { key: 'game_id',    label: 'Game',      render: v => v ?? '—' },
    { key: 'is_cancel',  label: 'Cancelled', render: v => <Tag label={v === 1 ? 'yes' : 'no'} /> },
    { key: 'created_at', label: 'Date',      render: v => formatDateTime(v) },
  ];

  const activeColumns =
    activeTab === 'bets' ? betColumns :
    activeTab === 'withdrawals' ? withdrawalColumns :
    depositColumns;

  // ── Loading / Error states ─────────────────────────────────────────────────
  if (isLoading && !data) return (
    <div className="ml-0 lg:ml-[256px] min-h-screen bg-[#0f1117] text-slate-100 animate-pulse">
      {/* Hero Banner Skeleton */}
      <div className="relative overflow-hidden border-b border-slate-800 bg-slate-900/30">
        <div className="relative px-8 pt-8 pb-6">
          {/* Breadcrumb skeleton */}
          <div className="h-4 w-32 bg-slate-800 rounded mb-5" />

          {/* Profile header row skeleton */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-slate-850" />
            <div className="flex-1 space-y-2.5">
              <div className="h-7 w-48 bg-slate-800 rounded" />
              <div className="flex gap-3 items-center">
                <div className="h-4 w-36 bg-slate-800 rounded" />
                <div className="h-3 w-px bg-slate-800" />
                <div className="h-4 w-20 bg-slate-800 rounded" />
                <div className="h-3 w-px bg-slate-800" />
                <div className="h-4 w-28 bg-slate-800 rounded" />
              </div>
            </div>
            <div className="h-9 w-24 bg-slate-800 rounded-lg" />
          </div>
        </div>
      </div>

      {/* Main Body Skeleton */}
      <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Left sidebar skeleton */}
        <div className="space-y-4">
          <div className="bg-slate-900/50 border border-slate-800/80 rounded-xl overflow-hidden p-4 space-y-3">
            <div className="h-3 w-24 bg-slate-800 rounded mb-2" />
            <div className="space-y-2">
              <div className="flex justify-between">
                <div className="h-3 w-16 bg-slate-800 rounded" />
                <div className="h-3 w-28 bg-slate-800 rounded" />
              </div>
              <div className="flex justify-between border-t border-slate-800/40 pt-2">
                <div className="h-3 w-16 bg-slate-800 rounded" />
                <div className="h-3 w-28 bg-slate-800 rounded" />
              </div>
              <div className="flex justify-between border-t border-slate-800/40 pt-2">
                <div className="h-3 w-20 bg-slate-800 rounded" />
                <div className="h-3 w-20 bg-slate-800 rounded" />
              </div>
            </div>
          </div>
        </div>

        {/* Right panel skeleton */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
          {/* Tabs header */}
          <div className="flex border-b border-slate-800 px-4 bg-slate-950/30 gap-6 h-12 items-center">
            <div className="h-4 w-16 bg-slate-800 rounded" />
            <div className="h-4 w-20 bg-slate-800 rounded" />
            <div className="h-4 w-12 bg-slate-800 rounded" />
            <div className="h-4 w-12 bg-slate-800 rounded" />
          </div>
          {/* Table placeholder */}
          <div className="p-4 space-y-3">
            <div className="h-8 bg-slate-800/40 rounded w-full" />
            <div className="h-10 bg-slate-800/20 rounded w-full" />
            <div className="h-10 bg-slate-800/20 rounded w-full" />
            <div className="h-10 bg-slate-800/20 rounded w-full" />
            <div className="h-10 bg-slate-800/20 rounded w-full" />
            <div className="h-10 bg-slate-800/20 rounded w-full" />
          </div>
        </div>
      </div>
    </div>
  );

  if (!profile) return (
    <div className="ml-0 lg:ml-[256px] min-h-screen bg-[#0f1117] flex items-center justify-center">
      <div className="text-center space-y-3">
        <p className="text-red-400 font-semibold">User not found.</p>
        <button onClick={() => navigate(-1)} className="text-indigo-400 text-sm hover:underline">← Back</button>
      </div>
    </div>
  );

  const pnl = parseFloat(profile.total_deposit) - parseFloat(profile.total_withdrawal);
  const initials = profile.name?.split(' ').map((w: any) => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <div className="ml-0 lg:ml-[256px] min-h-screen bg-[#0f1117] text-slate-100">

      {/* ── HERO BANNER ──────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950/80 via-[#0f1117] to-violet-950/60" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-0 right-1/4 w-64 h-64 bg-violet-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative px-8 pt-8 pb-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-5 text-xs text-slate-600">
            <button onClick={() => navigate(-1)} className="hover:text-slate-300 transition-colors flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
              Users
            </button>
            <span>/</span>
            <span className="text-slate-300 font-medium">{profile.name}</span>
          </div>

          {/* Profile header row */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-2xl font-black text-white shadow-2xl shadow-indigo-500/20">
                {initials}
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-[#0f1117]" />
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-extrabold text-white tracking-tight">{profile.name}</h1>
                {vipData && (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border shadow-sm"
                    style={{
                      backgroundColor: `${vipData.current_tier?.badge_color || '#94a3b8'}20`,
                      borderColor: `${vipData.current_tier?.badge_color || '#94a3b8'}50`,
                      color: vipData.current_tier?.badge_color || '#94a3b8',
                    }}
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
                    </svg>
                    {vipData.current_tier?.level_name || 'Standard'} (VIP {vipData.current_level})
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-1.5">
                <span className="text-slate-400 text-sm">{profile.email}</span>
                <span className="h-3 w-px bg-slate-700" />
                <span className="font-mono text-xs text-slate-500 bg-slate-800/80 px-2 py-0.5 rounded border border-slate-700">UID: {profile.id}</span>
                {profile.created && <>
                  <span className="h-3 w-px bg-slate-700" />
                  <span className="text-slate-500 text-xs">Joined {formatDate(profile.created, { month: 'short', year: 'numeric' })}</span>
                </>}
              </div>
            </div>

            <button onClick={() => refetch()} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-sm text-slate-300 font-medium transition-all">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
              Refresh
            </button>
          </div>

        </div>
      </div>

      {/* ── MAIN BODY ─────────────────────────────────────────────────────── */}
      <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">

        {/* Left sidebar */}
        <div className="space-y-4">

          {/* Account Details */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800">
              <h3 className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest">Account Details</h3>
            </div>
            <div className="divide-y divide-slate-800/50">
              {[
                { label: 'Registered On', value: formatDateTime(profile.created) },
                { label: 'IP Address',    value: profile.ip_address || '—' },
                { label: 'Location',      value: profile.geo || '—' },
                { label: 'Referral Code', value: profile.referalcode || '—' },
              ].map(row => (
                <div key={row.label} className="px-4 py-3 flex justify-between gap-3">
                  <span className="text-xs text-slate-600 shrink-0">{row.label}</span>
                  <span className="text-xs text-slate-200 font-mono font-medium break-all text-right">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── VIP PROGRESS GRAPH (Exact Stake Home Page Style Below Account Details) ── */}
          <div className="space-y-2">
            <h2 className="text-sm font-bold text-white flex items-center gap-2" style={{ textTransform: "capitalize" }}>
              <span style={{ width: "5px", height: "18px", borderRadius: "99px", backgroundColor: "#559cfc", display: "inline-block" }} />
              Your VIP Progress
            </h2>

            {/* Outer Card (Home Page Dark Container) */}
            <div
              style={{
                background: "#0E1D24",
                borderRadius: "10px",
                padding: "14px",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                boxShadow: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",
              }}
            >
              {/* Inner Progress Card (1:1 Home Page Theme) */}
              <div
                className="w-full"
                style={{
                  borderRadius: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  padding: "16px",
                  backgroundColor: "#112127",
                  position: "relative",
                }}
              >
                {/* Top Row: Username with blue chevron and Next level */}
                <div className="flex items-center justify-between w-full">
                  <span
                    className="flex items-center gap-1 text-white font-bold text-xs"
                    style={{ border: "none", background: "none", padding: 0 }}
                  >
                    {profile?.name || 'Player'}
                    <svg className="w-3.5 h-3.5 ml-0.5" style={{ color: "#559cfc" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>

                  {vipData?.next_tier ? (
                    <span style={{ fontSize: '11px', color: '#8e969f' }}>
                      Next level: <strong style={{ color: '#fff' }}>{vipData.next_tier.level_name}</strong>
                    </span>
                  ) : null}
                </div>

                {/* Progress Content — Conditional on active requirements */}
                {!vipData ? (
                  <div style={{ color: '#8e969f', fontSize: '11px', marginTop: '12px' }}>
                    Loading VIP Progress...
                  </div>
                ) : !vipData.next_tier ? (
                  <div className="flex justify-between items-center w-full text-xs text-white font-bold" style={{ marginTop: "12px" }}>
                    <span>100.00%</span>
                    <div style={{ fontSize: '11px', color: '#8e969f' }}>🏆 Max Level Reached</div>
                  </div>
                ) : vipData.next_tier.condition_type === 'both' ? (
                  /* Dual Progress Bars (Wager + Deposit) */
                  <div style={{ marginTop: '12px', width: '100%' }} className="space-y-2.5">
                    {/* Wager Block */}
                    <div style={{ background: 'rgba(255,255,255,0.02)', padding: '8px 10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '10px', color: '#8e969f', fontWeight: 'bold', textTransform: 'uppercase' }}>WAGER</span>
                        <span style={{ fontSize: '11px', color: '#559cfc', fontWeight: 'bold' }}>{vipData.wager_progress.toFixed(1)}%</span>
                      </div>
                      <div style={{ width: '100%', backgroundColor: '#1b2c38', height: '4px', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(100, vipData.wager_progress)}%`, backgroundColor: '#559cfc', transition: 'width 0.6s ease' }} />
                      </div>
                      <div style={{ fontSize: '10px', color: '#559cfc', marginTop: '4px', fontWeight: '600' }}>
                        {formatUSD(vipData.total_wagered_usd)} / {formatUSD(vipData.next_tier.required_amount)}
                      </div>
                    </div>

                    {/* Deposit Block */}
                    <div style={{ background: 'rgba(255,255,255,0.02)', padding: '8px 10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '10px', color: '#8e969f', fontWeight: 'bold', textTransform: 'uppercase' }}>DEPOSIT</span>
                        <span style={{ fontSize: '11px', color: '#24ee89', fontWeight: 'bold' }}>{vipData.deposit_progress.toFixed(1)}%</span>
                      </div>
                      <div style={{ width: '100%', backgroundColor: '#1b2c38', height: '4px', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(100, vipData.deposit_progress)}%`, backgroundColor: '#24ee89', transition: 'width 0.6s ease' }} />
                      </div>
                      <div style={{ fontSize: '10px', color: '#24ee89', marginTop: '4px', fontWeight: '600' }}>
                        {formatUSD(vipData.total_deposit_usd)} / {formatUSD(vipData.next_tier.required_deposit || vipData.next_tier.required_amount)}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Single Progress Bar (Condition: 'bet' or 'deposit') */
                  (() => {
                    const isDeposit = vipData.next_tier.condition_type === 'deposit';
                    const currentVal = isDeposit ? vipData.total_deposit_usd : vipData.total_wagered_usd;
                    const targetVal = parseFloat(isDeposit ? (vipData.next_tier.required_deposit || vipData.next_tier.required_amount || '0') : (vipData.next_tier.required_amount || '0'));
                    const pct = isDeposit ? vipData.deposit_progress : vipData.wager_progress;
                    const barColor = isDeposit ? '#24ee89' : '#559cfc';

                    return (
                      <div style={{ width: '100%', marginTop: '12px' }}>
                        <div className="flex items-center justify-between text-xs text-white font-bold mb-1.5">
                          <span style={{ fontSize: '10px', color: '#8e969f', textTransform: 'uppercase', fontWeight: 'bold' }}>
                            {isDeposit ? 'DEPOSIT' : 'WAGER'}
                          </span>
                          <span style={{ color: barColor, fontSize: '11px' }}>{pct.toFixed(1)}%</span>
                        </div>
                        <div style={{ width: '100%', backgroundColor: '#1b2c38', height: '4px', borderRadius: '99px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, backgroundColor: barColor, transition: 'width 0.6s ease' }} />
                        </div>
                        <div className="flex justify-between items-center mt-1.5 text-[10px] font-semibold" style={{ color: barColor }}>
                          <span>Progress: {pct.toFixed(1)}%</span>
                          <span>{formatUSD(currentVal)} / {formatUSD(targetVal)}</span>
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </div>


        </div>

        {/* Right panel — StakeTable */}
        <div className="flex flex-col gap-0 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">

          {/* Main tabs */}
          <div className="flex border-b border-slate-800 px-1 bg-slate-950/30">
            {['Deposits','Withdrawals','Bets','Bonus'].map(tab => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab.toLowerCase()); setPage(1); setSubTab('all'); }}
                className={`relative px-5 py-3.5 text-sm font-semibold transition-all ${
                  activeTab === tab.toLowerCase() ? 'text-white' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {tab}
                {activeTab === tab.toLowerCase() && (
                  <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-indigo-500 rounded-full" />
                )}
              </button>
            ))}
          </div>

          {/* Sub-tabs (only for deposit/withdrawal) */}
          {(activeTab === 'deposits' || activeTab === 'withdrawals') && (
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-slate-800">
              {['all','pending','accepted','completed','partially_paid','rejected'].map(s => (
                <button key={s} onClick={() => { setSubTab(s); setPage(1); }}
                  className={`px-3 py-1 rounded-md text-xs font-semibold capitalize transition-all ${
                    subTab === s ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-500/30' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  {s.replace('_', ' ')}
                </button>
              ))}
            </div>
          )}

          {/* ── StakeTable (reused component) ── */}
          <div className="p-4">
            <StakeTable
              title=""
              columns={activeColumns}
              data={txData?.data ?? []}
              isLoading={isFetching}
              hasSidebarMargin={false}
              emptyMessage="No records found for this tab."
              page={page}
              totalPages={totalPages}
              total={txData?.total ?? 0}
              limit={limit}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
